/* =========================================================
   SENPAI DRIP — Garment switcher
   Rail entrance, edge fades, cursor lean, prefetch,
   auto-rotate (infinite loop, starts on page load), and the
   sheet → full-page expand hand-off.
   Works with sd-quick-add.js events:
     sd:qa:ready  (sheet loaded a product)
     sd:qa:expand (swipe up / View full details in peek mode)

   Oct 2026: where the browser supports cross-page View
   Transitions (iOS 18.2+, Chrome 126+), the expand skips the
   curtain + ghost and hands the photo on screen to SDMorph
   (sd-motion.js) — it flies straight into the product gallery
   while the page loads. Older browsers keep the curtain expand.
   ========================================================= */
(function(){
  'use strict';
  if (window.SDSwitcher) return;

  var reduced  = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canHover = window.matchMedia('(hover:hover) and (pointer:fine)').matches;
  var mqMobile = window.matchMedia('(max-width:800px)');
  var prefetched = {};
  var fx = null;

  var GLIDE      = 850;   /* ms — one step glide */
  var IDLE       = 4000;  /* ms of no touching before rotation resumes */
  var HOVER_IDLE = 1500;  /* ms after the mouse leaves (desktop) */
  var FIRST_HOLD = 1200;  /* ms on the first card after load */

  /* ---------- helpers ---------- */
  function px(n){ return Math.max(0, Math.round(n)) + 'px'; }
  function slice(list){ return Array.prototype.slice.call(list); }

  function sized(src, w){
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function hiRes(d){
    var v = d && d.variant;
    var s = (v && v.featured_image && v.featured_image.src) ||
            (d && d.product && d.product.featured_image) || '';
    return s ? sized(s, 1000) : '';
  }

  function prefetch(url){
    if (!url || prefetched[url]) return;
    prefetched[url] = true;
    var l = document.createElement('link');
    l.rel = 'prefetch';
    l.href = url;
    document.head.appendChild(l);
  }

  /* soft spring with a small overshoot */
  function springEase(t){
    var c1 = 1.1, c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  }

  /* where the image lands before the page swaps */
  function target(){
    var iw = window.innerWidth, ih = window.innerHeight, w, h;
    if (mqMobile.matches){
      w = iw; h = w * 1.25;
      if (h > ih * .8){ h = ih * .8; w = h / 1.25; }
      return { x: (iw - w) / 2, y: Math.min(76, ih * .09), w: w, h: h };
    }
    h = Math.min(ih * .78, 720); w = h / 1.25;
    return { x: (iw - w) / 2, y: (ih - h) / 2, w: w, h: h };
  }

  /* ---------- auto-rotate setup ---------- */
  function autoSecs(el){
    if (reduced) return 0;
    var s = parseFloat(el.getAttribute('data-sd-sw-auto'));
    return s > 0 ? Math.max(1.5, s) : 0;
  }

  function cloneItem(it){
    var c = it.cloneNode(true);
    c.classList.add('is-clone');
    c.setAttribute('aria-hidden', 'true');
    c.removeAttribute('aria-current');
    if (c.tagName === 'BUTTON') c.tabIndex = -1;
    return c;
  }

  /* three identical sets (clone · real · clone) so the rail loops forever */
  function buildLoop(rail){
    var items = slice(rail.children).filter(function(c){ return c.classList.contains('sd-sw__item'); });
    if (items.length < 2 || rail.scrollWidth <= rail.clientWidth + 4) return false;
    var pre = document.createDocumentFragment();
    var post = document.createDocumentFragment();
    items.forEach(function(it){
      pre.appendChild(cloneItem(it));
      post.appendChild(cloneItem(it));
    });
    rail.insertBefore(pre, rail.firstChild);
    rail.appendChild(post);
    return true;
  }

  function sheetOpen(){
    var roots = document.querySelectorAll('.sd-qa');
    for (var k = 0; k < roots.length; k++){
      var r = roots[k];
      if (r.hidden || r.getAttribute('aria-hidden') === 'true') continue;
      if (r.getAttribute('aria-hidden') === 'false') return true;
      if (/\bis-(open|active|visible|shown)\b/.test(r.className) || r.hasAttribute('open')) return true;
    }
    return false;
  }

  function initAuto(el, rail, secs, loop){
    var items = slice(rail.querySelectorAll('.sd-sw__item'));
    var n = loop ? items.length / 3 : items.length;
    if (n < 2) return;

    var st = { hold:false, hover:false, kb:false, qa:false, cool:false };
    var running = false, focus = -1, gliding = false, lastProg = 0;
    var timer = 0, coolT = 0, qaT = 0, settleT = 0, raf = 0;

    el.classList.add('has-auto');
    el.style.setProperty('--sw-auto', secs + 's');

    function setScroll(v){ lastProg = performance.now(); rail.scrollLeft = v; }
    function setW(){ return loop ? items[n].offsetLeft - items[0].offsetLeft : 0; }

    function targetFor(i){
      var it = items[i], max = rail.scrollWidth - rail.clientWidth;
      return Math.min(max, Math.max(0, it.offsetLeft - (rail.clientWidth - it.offsetWidth) / 2));
    }

    function nearest(){
      var c = rail.scrollLeft + rail.clientWidth / 2, best = 0, d = Infinity;
      items.forEach(function(it, i){
        var dd = Math.abs(it.offsetLeft + it.offsetWidth / 2 - c);
        if (dd < d){ d = dd; best = i; }
      });
      return best;
    }

    function setFocus(i, glint){
      if (focus > -1) items[focus].classList.remove('is-focus', 'is-ticking', 'is-glint');
      focus = i;
      var it = items[i];
      it.classList.add('is-focus');
      if (glint){ void it.offsetWidth; it.classList.add('is-glint'); }
    }

    function tick(){
      var it = items[focus];
      if (!it) return;
      it.classList.remove('is-ticking');
      void it.offsetWidth;
      it.classList.add('is-ticking');
    }

    function stopGlide(){
      cancelAnimationFrame(raf);
      if (gliding){ gliding = false; rail.classList.remove('is-gliding'); }
    }

    function glideTo(i, done){
      stopGlide();
      setFocus(i, true);
      var from = rail.scrollLeft, to = targetFor(i);
      if (Math.abs(to - from) < 1){ if (done) done(); return; }
      gliding = true;
      rail.classList.add('is-gliding');
      var t0 = performance.now();
      raf = requestAnimationFrame(function frame(now){
        var p = Math.min(1, Math.max(0, (now - t0) / GLIDE));
        setScroll(from + (to - from) * springEase(p));
        if (p < 1){ raf = requestAnimationFrame(frame); return; }
        gliding = false;
        rail.classList.remove('is-gliding');
        if (done) done();
      });
    }

    /* jump a whole set left/right — looks identical, so it's invisible */
    function jump(shift, then){
      var w = setW();
      el.classList.add('is-snap');
      rail.classList.add('is-gliding');
      setScroll(rail.scrollLeft + (shift > 0 ? w : -w));
      if (focus > -1) setFocus(focus + shift, false);
      requestAnimationFrame(function(){
        requestAnimationFrame(function(){
          el.classList.remove('is-snap');
          if (!gliding) rail.classList.remove('is-gliding');
          if (then) then();
        });
      });
    }

    function normalizeFocus(then){
      if (!loop || focus < 0) return then();
      if (focus >= 2 * n) return jump(-n, then);
      if (focus < n) return jump(n, then);
      then();
    }

    /* after a manual swipe, recentre on the middle set */
    function settle(){
      if (!loop || st.hold || gliding) return;
      var w = setW(), sl = rail.scrollLeft;
      if (sl < w * .5) jump(n);
      else if (sl > w * 1.5) jump(-n);
    }

    function schedule(ms){
      ms = ms || secs * 1000;
      el.style.setProperty('--sw-auto', ms + 'ms');
      clearTimeout(timer);
      timer = setTimeout(step, ms);
    }

    function step(){
      timer = 0;
      if (!running) return;
      normalizeFocus(function(){
        if (!running) return;
        var next = focus + 1;
        if (next >= items.length) next = 0;
        glideTo(next, function(){ if (running){ tick(); schedule(); } });
      });
    }

    function canRun(){
      return !document.hidden && !st.hold && !st.hover && !st.kb && !st.qa && !st.cool;
    }

    function update(){
      var ok = canRun();
      if (ok === running) return;
      running = ok;
      el.classList.toggle('is-auto', ok);
      if (ok){
        glideTo(nearest(), function(){ if (running){ tick(); schedule(FIRST_HOLD); } });
      } else {
        clearTimeout(timer); timer = 0;
        stopGlide();
        if (focus > -1) items[focus].classList.remove('is-ticking', 'is-glint');
      }
    }

    function cooldown(ms){
      st.cool = true;
      clearTimeout(coolT);
      update();
      coolT = setTimeout(function(){ st.cool = false; update(); }, ms || IDLE);
    }

    /* touch — only a sideways swipe on the row counts as browsing;
       a vertical page scroll that passes over it is ignored */
    var tx = 0, ty = 0, tDir = '';
    function release(){
      if (!st.hold) return;
      st.hold = false;
      cooldown();
    }
    rail.addEventListener('touchstart', function(e){
      var t = e.touches[0];
      tx = t.clientX; ty = t.clientY; tDir = '';
    }, { passive: true });
    rail.addEventListener('touchmove', function(e){
      if (tDir) return;
      var t = e.touches[0];
      var dx = Math.abs(t.clientX - tx), dy = Math.abs(t.clientY - ty);
      if (dx < 6 && dy < 6) return;
      tDir = dx > dy ? 'x' : 'y';
      if (tDir === 'x'){ st.hold = true; cooldown(); }
    }, { passive: true });
    rail.addEventListener('touchend', release, { passive: true });
    rail.addEventListener('touchcancel', release, { passive: true });

    /* mouse drag / wheel */
    rail.addEventListener('pointerdown', function(e){
      if (e.pointerType === 'mouse'){ st.hold = true; cooldown(); }
    });
    window.addEventListener('pointerup', function(e){
      if (e.pointerType === 'mouse') release();
    });
    rail.addEventListener('wheel', function(e){
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) cooldown();
    }, { passive: true });

    /* manual scroll of the row (not ours) */
    rail.addEventListener('scroll', function(){
      if (performance.now() - lastProg < 120) return;
      cooldown();
      clearTimeout(settleT);
      settleT = setTimeout(settle, 180);
    }, { passive: true });

    /* desktop hover */
    if (canHover){
      el.addEventListener('pointerenter', function(){ st.hover = true; update(); });
      el.addEventListener('pointerleave', function(){ st.hover = false; cooldown(HOVER_IDLE); });
    }

    /* keyboard */
    el.addEventListener('focusin', function(){ st.kb = true; update(); });
    el.addEventListener('focusout', function(e){
      if (e.relatedTarget && el.contains(e.relatedTarget)) return;
      st.kb = false;
      cooldown();
    });

    /* quick-look sheet open → hold still until it closes */
    rail.addEventListener('click', function(e){
      if (!e.target.closest('[data-sd-sw-item]')) return;
      st.qa = true;
      update();
      var t0 = Date.now();
      clearInterval(qaT);
      qaT = setInterval(function(){
        if (Date.now() - t0 < 1200 || sheetOpen()) return;
        clearInterval(qaT);
        st.qa = false;
        cooldown(HOVER_IDLE);
      }, 400);
    });

    /* pause only while the browser tab is hidden */
    document.addEventListener('visibilitychange', update);

    /* keep the focused cut centred through rotation / resize */
    window.addEventListener('resize', function(){
      if (!running || focus < 0 || gliding) return;
      setScroll(targetFor(focus));
    }, { passive: true });

    /* go — starts the moment the page loads */
    update();
  }

  /* ---------- rail ---------- */
  function initRail(el){
    if (el.__sdsw) return;
    el.__sdsw = true;

    var rail = el.querySelector('.sd-sw__rail');
    if (!rail) return;

    var secs = autoSecs(el);
    var loop = secs > 0 && buildLoop(rail);

    /* centre the current garment inside the rail (never scrolls the page) */
    var cur = rail.querySelector('.is-current:not(.is-clone)');
    if (cur && rail.scrollWidth > rail.clientWidth){
      rail.scrollLeft = cur.offsetLeft - (rail.clientWidth - cur.offsetWidth) / 2;
    }

    function edges(){
      el.classList.toggle('has-l', rail.scrollLeft > 4);
      el.classList.toggle('has-r', rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 4);
    }
    edges();
    rail.addEventListener('scroll', edges, { passive: true });
    window.addEventListener('resize', edges, { passive: true });

    /* staggered entrance */
    if (reduced || !('IntersectionObserver' in window)){
      el.classList.add('is-in');
    } else {
      el.classList.add('is-armed');
      var io = new IntersectionObserver(function(entries){
        entries.forEach(function(en){
          if (!en.isIntersecting) return;
          el.classList.add('is-in');
          io.disconnect();
        });
      }, { threshold: .2 });
      io.observe(el);
    }

    /* prefetch the full page the moment intent shows */
    el.querySelectorAll('[data-sd-sw-item]').forEach(function(b){
      var url = b.getAttribute('data-sd-sw-url');
      b.addEventListener('pointerenter', function(){ prefetch(url); }, { passive: true });
      b.addEventListener('touchstart', function(){ prefetch(url); }, { passive: true });
    });

    /* cursor lean (desktop) */
    if (canHover && !reduced){
      el.querySelectorAll('.sd-sw__item:not(.is-current) .sd-sw__frame').forEach(function(f){
        f.addEventListener('pointermove', function(e){
          var r = f.getBoundingClientRect();
          var x = (e.clientX - r.left) / r.width - .5;
          var y = (e.clientY - r.top) / r.height - .5;
          f.style.setProperty('--ry', (x * 14).toFixed(2) + 'deg');
          f.style.setProperty('--rx', (y * -14).toFixed(2) + 'deg');
        });
        f.addEventListener('pointerleave', function(){
          f.style.removeProperty('--rx');
          f.style.removeProperty('--ry');
        });
      });
    }

    if (secs > 0) initAuto(el, rail, secs, loop);
  }

  function initAll(){
    document.querySelectorAll('[data-sd-sw]').forEach(initRail);
  }

  /* ---------- expand: sheet → full page ---------- */
  function cleanup(){
    if (!fx) return;
    fx.nodes.forEach(function(n){ n.remove(); });
    fx.root.classList.remove('is-expanding');
    fx = null;
  }

  /* The photo the shopper is looking at in the peek sheet:
     full view → the gallery slide centred in the track (once loaded)
     half view → the thumbnail the sheet hands us */
  function shotOf(d){
    var root = d.sheet && d.sheet.closest('.sd-qa');
    var track = d.sheet && d.sheet.querySelector('[data-sd-qa-track]');
    if (root && track && root.classList.contains('is-full') && track.children.length){
      var c = track.scrollLeft + track.clientWidth / 2, best = null, dist = Infinity;
      for (var i = 0; i < track.children.length; i++){
        var s = track.children[i];
        var dd = Math.abs(s.offsetLeft + s.offsetWidth / 2 - c);
        if (dd < dist){ dist = dd; best = s; }
      }
      var im = best && best.querySelector('img');
      if (im && im.complete && im.naturalWidth) return im;
    }
    return d.img || null;
  }

  /* Morph browsers: name the photo and navigate now. The browser keeps
     this frame on screen until the product page is ready, then the
     photo flies into the gallery. No curtain, no ghost, no wait. */
  function morph(d){
    var M = window.SDMorph;
    if (!M || !M.can || !d.url) return false;
    var root = d.sheet && d.sheet.closest('.sd-qa');
    if (root) root.classList.add('is-expanding'); /* locks the sheet while the page loads */
    M.name(shotOf(d));
    window.location.href = d.url;
    return true;
  }

  function run(d){
    if (fx) return;
    var root = d.sheet && d.sheet.closest('.sd-qa');
    if (!root){ window.location.href = d.url; return; }

    var gone = false;
    function go(){
      if (gone) return;
      gone = true;
      window.location.href = d.url;
    }

    fx = { root: root, nodes: [] };
    root.classList.add('is-expanding');

    var iw = window.innerWidth, ih = window.innerHeight;
    var r = d.sheet.getBoundingClientRect();
    var rad = mqMobile.matches ? '26px 26px 0px 0px' : '24px 24px 24px 24px';

    /* glass curtain grows out of the sheet to fill the screen */
    var curtain = document.createElement('div');
    curtain.className = 'sd-sw-curtain';
    document.body.appendChild(curtain);
    fx.nodes.push(curtain);

    var clipFrom = 'inset(' + px(r.top) + ' ' + px(iw - r.right) + ' ' + px(ih - r.bottom) + ' ' + px(r.left) + ' round ' + rad + ')';
    var clipTo   = 'inset(0px 0px 0px 0px round 0px 0px 0px 0px)';
    curtain.style.clipPath = clipTo;

    var ca = null;
    if (curtain.animate){
      ca = curtain.animate(
        [{ clipPath: clipFrom }, { clipPath: clipTo }],
        { duration: 540, easing: 'cubic-bezier(.7,0,.2,1)', fill: 'both' }
      );
    }

    /* the thumbnail scales up into hero position */
    var src = d.img;
    if (src && (src.currentSrc || src.src)){
      var a = src.getBoundingClientRect();
      var t = target();
      var g = document.createElement('img');
      g.className = 'sd-sw-ghost';
      g.alt = '';
      g.decoding = 'async';
      g.src = src.currentSrc || src.src;
      g.style.left = t.x + 'px';
      g.style.top = t.y + 'px';
      g.style.width = t.w + 'px';
      g.style.height = t.h + 'px';
      document.body.appendChild(g);
      fx.nodes.push(g);

      var hi = hiRes(d);
      if (hi){
        var pre = new Image();
        pre.onload = function(){ g.src = hi; };
        pre.src = hi;
      }

      if (g.animate && a.width > 0){
        var s = a.width / t.w;
        g.animate([
          { transform: 'translate(' + (a.left - t.x) + 'px,' + (a.top - t.y) + 'px) scale(' + s + ')', borderRadius: (14 / s) + 'px' },
          { transform: 'translate(0px,0px) scale(1)', borderRadius: '18px' }
        ], { duration: 640, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both' });
      }
    }

    if (ca) ca.onfinish = function(){ setTimeout(go, 80); };
    else setTimeout(go, 80);
    setTimeout(go, 1100); /* safety net */
  }

  /* ---------- events ---------- */
  document.addEventListener('sd:qa:expand', function(e){
    if (reduced || !e.detail) return; /* quick-add navigates plainly */
    e.preventDefault();
    if (morph(e.detail)) return;      /* photo flies into the product page */
    run(e.detail);                    /* older browsers: curtain expand */
  });

  document.addEventListener('sd:qa:ready', function(e){
    var d = e.detail;
    if (!d || !d.peek) return;
    var hi = hiRes(d);
    if (hi) (new Image()).src = hi;
    prefetch(d.url);
  });

  /* back button from the next page (bfcache) — restore a clean sheet */
  window.addEventListener('pageshow', function(e){
    if (!e.persisted) return;
    cleanup();
    document.querySelectorAll('.sd-qa.is-expanding').forEach(function(r){
      r.classList.remove('is-expanding');
    });
    if (window.SDQuickAdd) window.SDQuickAdd.close();
  });

  document.addEventListener('shopify:section:load', initAll);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  window.SDSwitcher = { init: initAll };
})();